import React, { PropsWithChildren } from 'react';
import { Tooltip } from '@fullstory/components';
import { PageDefinition, SuggestionStatus } from '@fullstory/proto/fs/api/pages';
import { PageDefinitionBody } from './PageDefinitionBody';
import { ExploreMenu, ResourceDisplayPane } from '@fullstory/library-ui';
import { getPageMetadata, getPageName } from '../../utils/utils';
import { useFlags } from '@fullstory/react-hooks';
import { SegmentSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { PageSuggestionTooltipContent } from './PageSuggestionTooltipContent';

export interface PageDefinitionTooltipProps {
  pageDefinition: PageDefinition;
  segmentSettings?: SegmentSettings;
  onEdit?: () => void;
  canAccess?: boolean;
  exploreMenu?: boolean;
  placement?: React.ComponentProps<typeof Tooltip>['placement'];
}

export const PageDefinitionTooltip: React.FC<PropsWithChildren<PageDefinitionTooltipProps>> = ({
  pageDefinition,
  segmentSettings,
  onEdit,
  canAccess,
  exploreMenu = true,
  placement = 'right',
  children,
}) => {
  const flags = useFlags();
  const hasFSTA = flags.hasBit(Flag.FeatFSTA);

  const shouldShowPageSuggestionTooltipContent =
    flags.featureFlags().AppAiPageSuggestions &&
    flags.featureFlags().AppAiPageExplorations &&
    pageDefinition.suggestion?.status === SuggestionStatus.STATUS_PENDING;

  return (
    <Tooltip
      disablePadding
      placement={placement}
      maxWidth={400}
      showDelay={300}
      persistDelay={100}
      tooltipContent={
        shouldShowPageSuggestionTooltipContent ? (
          <PageSuggestionTooltipContent pageDefinition={pageDefinition} onEdit={onEdit} />
        ) : (
          <ResourceDisplayPane tooltip>
            <ResourceDisplayPane.Header
              label={getPageName(pageDefinition)}
              onOpen={onEdit}
              canAccess={canAccess}
              actionComponent={
                exploreMenu && flags.featureFlags().AppGlidePaths && hasFSTA ? (
                  <ExploreMenu pageDefinition={pageDefinition} segmentSettings={segmentSettings} />
                ) : undefined
              }
            />
            <ResourceDisplayPane.Body>
              <PageDefinitionBody pageDefinition={pageDefinition} />
            </ResourceDisplayPane.Body>
            {pageDefinition.id !== 0 ? (
              <ResourceDisplayPane.Footer metadata={getPageMetadata(pageDefinition)} />
            ) : null}
          </ResourceDisplayPane>
        )
      }
      variant="light"
      childrenOverflowHidden
    >
      {children}
    </Tooltip>
  );
};
